/**
 * Page visits of signed-in people (V076), for Admin → Activity log.
 *
 * Each route change adds the page's path (never the query string or hash, which
 * can carry tokens or search text) to a small queue, sent every few seconds and
 * when the tab is hidden, fire-and-forget. Nothing is recorded for signed-out
 * visitors or in an admin's "View as" tab (the server refuses a view token's
 * writes anyway). A failed send is dropped: this must never get in the way.
 */
import { API_BASE } from "./config";
import { getAccessToken, getSession, isViewTab } from "./session";

export const VISITS_PATH = "/platform/activity/visits";
const FLUSH_MS = 5_000;
const MAX_BATCH = 50; // the server's cap per request
const MAX_QUEUE = 200; // offline for long: keep the newest
const SESSION_KEY = "rai.visit-session";

interface Visit {
  path: string;
  at: string;
}

let queue: Visit[] = [];
let last: string | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let session: string | null = null;
// Off under Vitest (page tests count their API calls); a tracker test turns it on.
let enabled = import.meta.env.MODE !== "test";

/** One id per browser tab (kept across reloads of that tab). */
function sessionId(): string {
  if (session) return session;
  let id: string | null = null;
  try {
    id = sessionStorage.getItem(SESSION_KEY);
  } catch {
    // storage blocked: a fresh id per page load
  }
  if (!id || !/^[A-Za-z0-9-]{8,40}$/.test(id)) {
    id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      sessionStorage.setItem(SESSION_KEY, id);
    } catch {
      // as above
    }
  }
  session = id;
  return id;
}

/** "/app/stocks/RELIANCE?tab=chart#x" → "/app/stocks/RELIANCE". */
export function routePath(pathname: string): string {
  const p = pathname.split(/[?#]/)[0] ?? "/";
  return p.length > 1 ? p.replace(/\/+$/, "") || "/" : p || "/";
}

function schedule(): void {
  if (timer === null) timer = setTimeout(() => flushVisits(), FLUSH_MS);
}

/** Send what's queued. `keepalive` lets the request outlive a closing tab. */
export function flushVisits(keepalive = false): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (queue.length === 0) return;
  const token = getAccessToken();
  if (!token || getSession().status !== "signed-in") {
    // being refreshed (or signed out meanwhile): try again shortly, or drop on close
    if (keepalive || getSession().status === "signed-out") queue = [];
    else schedule();
    return;
  }
  const visits = queue.splice(0, MAX_BATCH);
  if (queue.length > 0) schedule();
  try {
    void fetch(`${API_BASE}${VISITS_PATH}`, {
      method: "POST",
      keepalive,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ session: sessionId(), visits }),
    }).catch(() => undefined);
  } catch {
    // fetch itself unavailable: nothing to do
  }
}

/** The router moved to `pathname`: queue it for a signed-in person. */
export function recordVisit(pathname: string): void {
  const s = getSession();
  if (!enabled || isViewTab() || s.status !== "signed-in") return;
  const path = routePath(pathname);
  // per person: someone else signing in on the same page still counts
  const key = `${s.user?.id ?? ""}:${path}`;
  if (key === last) return;
  last = key;
  queue.push({ path, at: new Date().toISOString() });
  if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
  if (queue.length >= MAX_BATCH) flushVisits();
  else schedule();
}

let listening = false;

/** Send the queue when the tab is hidden or closed (once per page load). */
export function listenForPageHide(): void {
  if (listening || typeof document === "undefined") return;
  listening = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushVisits(true);
  });
  window.addEventListener("pagehide", () => flushVisits(true));
}

/** Tests only: turn tracking on or off and forget what was queued. */
export function resetVisitsForTests(on: boolean): void {
  enabled = on;
  queue = [];
  last = null;
  session = null;
  if (timer !== null) clearTimeout(timer);
  timer = null;
}
